import React, { FC, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { Box, Stack, SxProps } from "@mui/material";

import { AlbumAuthorDetails, AlbumAuthorName } from "@pages/Catalog/components/components";

import { FiltersType, navigateToCatalogWithQuery } from "@hooks/useFiltersQuery/utils";

import { IComposer } from "@models/models";
import uniqueId from "lodash/uniqueId";

import { Avatar } from "@/ui-kit";

import * as Styled from "./styles";

type TAvatarGroup = {
  composers: IComposer[] | string[];
  isSmall?: boolean;
  isCollapsed?: boolean;
  isNamesHidden?: boolean;
  isDetails?: boolean;
  doesNeedAlignment?: boolean;
  isSmallDesktop?: boolean;
  sx?: SxProps;
};

export const AvatarGroup: FC<TAvatarGroup> = ({
  composers,
  isSmall = false,
  isDetails = false,
  doesNeedAlignment = false,
  isSmallDesktop = false,
  isCollapsed = false,
  isNamesHidden = false,
  sx,
}) => {
  const navigate = useNavigate();

  const handleNavigateComposer = (composerName?: string) => {
    if (!composerName) return;
    navigate(navigateToCatalogWithQuery(composerName, FiltersType.COMPOSER));
  };

  if (!composers) {
    return null;
  }

  if (composers?.length === 1) {
    return (
      <Avatar
        doesNeedAlignment={doesNeedAlignment}
        composerName={composers[0] as unknown as string}
        isSmall={isSmall}
        isSmallDesktop={isSmallDesktop}
        onClick={() => handleNavigateComposer(typeof composers[0] === "object" ? composers[0].name : composers[0])}
        sx={sx}
      >
        {isDetails ? (
          <AlbumAuthorDetails
            underline="hover"
            onClick={() => handleNavigateComposer(typeof composers[0] === "object" ? composers[0].name : composers[0])}
            sx={{ alignSelf: "center" }}
          >
            {typeof composers[0] === "object" ? composers[0].name : composers[0]}
          </AlbumAuthorDetails>
        ) : (
          <AlbumAuthorName
            onClick={() => handleNavigateComposer(typeof composers[0] === "object" ? composers[0].name : composers[0])}
          >
            {typeof composers[0] === "object" ? composers[0].name : composers[0]}
          </AlbumAuthorName>
        )}
      </Avatar>
    );
  }

  return isDetails ? (
    <Stack direction="row" alignItems="center" spacing="8px" width="100%" minHeight={isSmall ? "24px" : "32px"}>
      <Styled.AvatarGroupWrapper isDetails={isDetails} $isSmall={isSmall} max={3} total={composers.length}>
        {composers.map((composer) => (
          <Box display="flex" gap="8px">
            <Avatar
              onClick={() => handleNavigateComposer(composer)}
              composerName={composer}
              key={uniqueId("avatar-group_")}
              isSmall={isSmall}
              isGroup
            />
            {isDetails ? (
              <AlbumAuthorDetails underline="hover" onClick={() => handleNavigateComposer(composer)}>
                {composer}
              </AlbumAuthorDetails>
            ) : (
              <AlbumAuthorName onClick={() => handleNavigateComposer(composer)}>{composer}</AlbumAuthorName>
            )}
          </Box>
        ))}
      </Styled.AvatarGroupWrapper>
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" spacing="8px">
      <Styled.AvatarGroupWrapper isDetails={isDetails} $isSmall={isSmall} max={3} total={composers.length}>
        {composers.map((composer) => (
          <Avatar
            onClick={() => handleNavigateComposer(composer)}
            composerName={composer}
            key={uniqueId("avatar-group_")}
            isSmall={isSmall}
            isGroup
          />
        ))}
      </Styled.AvatarGroupWrapper>
      {!isCollapsed && (
        <Stack direction="row" alignItems="center" spacing="4px">
          {composers.length > 3 || isNamesHidden
            ? null
            : composers.map((composer, index) => (
                <Fragment key={uniqueId(`${composer}_`)}>
                  {isDetails ? (
                    <AlbumAuthorDetails underline="hover" onClick={() => handleNavigateComposer(composer)}>
                      {composer}
                    </AlbumAuthorDetails>
                  ) : (
                    <AlbumAuthorName onClick={() => handleNavigateComposer(composer)}>{composer}</AlbumAuthorName>
                  )}
                  {index !== composers.length - 1 && <span style={{ color: "#FBF4EC" }}>&</span>}
                </Fragment>
              ))}
        </Stack>
      )}
    </Stack>
  );
};
